import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { initSentry, SentryErrorBoundary } from 'lib/sentry';
import { initConsentMode } from 'lib/consent-mode';
import { initAttribution, initAttributionBeacon } from 'lib/attribution';
import BreakpointsProvider from 'providers/BreakpointsProvider';
import NotistackProvider from 'providers/NotistackProvider';
import SettingsPanelProvider from 'providers/SettingsPanelProvider';
import SettingsProvider from 'providers/SettingsProvider';
import ThemeProvider from 'providers/ThemeProvider';
import router from 'routes/router';
import { reloadForChunkError } from 'routes/lazyWithReload';
import SWRConfiguration from 'services/configuration/SWRConfiguration';
import './locales/i18n';

initSentry();

// Tell Google what the visitor has already agreed to, and keep it in step with
// every later change. index.html has already declared the deny-by-default state
// inline (it has to run before GTM); this is the earliest a bundled module can
// upgrade that for a returning visitor. Before React renders, so an accepted
// visitor is not measured cookielessly while the app boots.
initConsentMode();

// Read the campaign parameters off the URL before React renders — the first
// client-side navigation replaces the query string, and with it the only
// record of which ad brought this visitor. Nothing is stored unless marketing
// consent allows it; until then the touch is held in memory and flushed if the
// visitor accepts.
initAttribution();

// Hand whatever was captured to the server, which then owns it — at most one
// request per campaign touch, never on an ordinary page view, and never without
// marketing consent. A successful handoff deletes the browser's staging copy, so
// from that moment the authoritative record is the server's alone.
initAttributionBeacon({ apiUrl: import.meta.env.VITE_API_URL });

// Recover from stale dynamic-import chunks after a redeploy. Vite dispatches
// `vite:preloadError` on window whenever a dynamically imported module fails to
// load — route chunks and feature chunks alike (exceljs, heic2any, lazy API
// services). The still-open old tab references content-hashed filenames that
// 404 once the new build is live, so the SPA fallback serves index.html and the
// browser refuses to run it as a module. A single guarded reload pulls the
// fresh index.html; preventDefault stops Vite from rethrowing once handled.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadForChunkError();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <SentryErrorBoundary
    fallback={
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Something went wrong. Please refresh the page.
      </div>
    }
  >
    <React.StrictMode>
      <SWRConfiguration>
        <SettingsProvider>
          <ThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <NotistackProvider>
                <BreakpointsProvider>
                  <SettingsPanelProvider>
                    <RouterProvider router={router} />
                  </SettingsPanelProvider>
                </BreakpointsProvider>
              </NotistackProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </SettingsProvider>
      </SWRConfiguration>
    </React.StrictMode>
  </SentryErrorBoundary>,
);

// Hide the pre-React splash once the first paint has committed.
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    const splash = document.getElementById('initial-splash');
    if (!splash) return;
    splash.classList.add('is-hidden');
    splash.addEventListener('transitionend', () => splash.remove(), { once: true });
    // Safety net in case transitionend doesn't fire (e.g. reduced motion).
    setTimeout(() => splash.remove(), 600);
  });
});
